/**
 * Client-side validation for server-rendered forms (`form[data-validate]`).
 *
 * The native Constraint Validation API supplies every verdict; this module
 * only decides when to ask and how to paint the result. Painting follows the
 * same DOM contract `ActionView::Base.field_error_proc`
 * (config/initializers/actionview_form_errors.rb) uses for server-rendered
 * errors — `.error` on the field's wrapper reveals the `small.error` message
 * inside it — so client- and server-produced errors on the same form look
 * identical.
 */

const FORM_SELECTOR = 'form[data-validate]';

/**
 * How long after the user last touches a field it is (re)validated.
 *
 * This delay is also why the reveal is not keyed off CSS `:user-invalid`,
 * which would otherwise remove the paint function and three listeners here:
 * `:user-invalid` arms on blur, while the customer-facing forms reveal a
 * message one second after the last keystroke.
 */
const LIVE_VALIDATE_DELAY_MS = 1000;

type ValidatableField =
  | HTMLInputElement
  | HTMLSelectElement
  | HTMLTextAreaElement;

let liveTimer: ReturnType<typeof setTimeout> | null = null;
let initialized = false;

export default function initializeRailsFormValidation(): void {
  if (initialized) return;
  initialized = true;

  // `novalidate` keeps the browser's own bubbles out of the way so the
  // translated `small.error` message is the only feedback. `field.validity`
  // keeps working regardless.
  document.querySelectorAll<HTMLFormElement>(FORM_SELECTOR).forEach(form => {
    form.noValidate = true;
  });

  // Capture phase: Rails UJS, loaded earlier by `boot/vendor.js`, also listens
  // for `submit` on `document`, and would otherwise see a blocked submit first.
  // It asks the form's `data-confirm` question and disables its
  // `data-disable-with` buttons, leaving the form stuck.
  document.addEventListener('submit', handleSubmit, true);
  document.addEventListener('change', handleFieldEvent);
  document.addEventListener('keydown', handleKeydown);
  // Capture phase: `blur` does not bubble, so a delegated listener on
  // `document` only sees it on the way down.
  document.addEventListener('blur', handleFieldEvent, true);
  // `invalid` does not bubble either.
  document.addEventListener('invalid', handleInvalid, true);
}

/**
 * A form still under native validation never reaches `handleSubmit`: the
 * browser validates first, blocks the submit itself, and fires `invalid` on
 * each failing control instead. Cancelling that event suppresses the
 * browser's own untranslated bubble, so the translated `small.error` message
 * stays the only feedback, and adopting the form here puts every later
 * attempt back on the ordinary path.
 *
 * This is the only unconditional guarantee for a form rendered after boot:
 * the init-time walk never saw it, and `scheduleLiveValidation` adopts it
 * only once a field has been focused and left again. A dialog that opens
 * with a validatable field focused satisfies that by accident -- one whose
 * first focusable is a button does not.
 */
function handleInvalid(event: Event): void {
  const target = event.target;
  if (!(target instanceof Element) || !isValidatableField(target)) return;

  const form = target.form;
  if (!form || !form.matches(FORM_SELECTOR)) return;

  // The browser fires `invalid` at each failing control in tree order, so
  // only the first of a submit finds the form not yet adopted. Cancelling the
  // browser's report also cancels the focus move that came with it.
  const firstOfSubmit = !form.noValidate;
  event.preventDefault();
  form.noValidate = true;
  paintValidity(target);
  if (firstOfSubmit) {
    candidateFields(form)
      .find(field => !field.validity.valid)
      ?.focus();
  }
}

function handleSubmit(event: Event): void {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) {
    return;
  }

  if (liveTimer !== null) {
    clearTimeout(liveTimer);
    liveTimer = null;
  }

  const fields = candidateFields(form);
  fields.forEach(paintValidity);

  const firstInvalid = fields.find(field => !field.validity.valid);
  if (firstInvalid) {
    event.preventDefault();
    event.stopPropagation();
    form.setAttribute('data-invalid', '');
    firstInvalid.focus();
  } else {
    // Only cleared here, on the next submit attempt - nothing in this
    // codebase reads form[data-invalid], so a stale attribute between
    // submits carries no visible or functional effect.
    form.removeAttribute('data-invalid');
  }
}

function handleFieldEvent(event: Event): void {
  scheduleLiveValidation(event.target);
}

function handleKeydown(event: KeyboardEvent): void {
  // Tab is navigation, not editing, so it schedules nothing here. The blur it
  // causes still does — a field is judged on being left, however it was left.
  if (event.key === 'Tab') return;
  scheduleLiveValidation(event.target);
}

function scheduleLiveValidation(target: EventTarget | null): void {
  if (
    !(target instanceof HTMLInputElement) &&
    !(target instanceof HTMLSelectElement) &&
    !(target instanceof HTMLTextAreaElement)
  ) {
    return;
  }

  const form = target.form;
  if (!form || !form.matches(FORM_SELECTOR)) return;
  // Adopts a form rendered after boot that the init-time walk missed.
  form.noValidate = true;

  if (liveTimer !== null) clearTimeout(liveTimer);
  // One shared timer: touching another field abandons the pending
  // validation, so only the last-touched field is judged.
  liveTimer = setTimeout(() => {
    liveTimer = null;
    if (isVisible(target)) paintValidity(target);
  }, LIVE_VALIDATE_DELAY_MS);
}

function candidateFields(form: HTMLFormElement): Array<ValidatableField> {
  return Array.from(form.elements)
    .filter(isValidatableField)
    .filter(field => field.willValidate)
    .filter(isVisible);
}

function isValidatableField(el: Element): el is ValidatableField {
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLSelectElement ||
    el instanceof HTMLTextAreaElement
  );
}

/**
 * Whether the field can currently be seen. Fields inside a hidden container
 * must not gate the submit: `#venue-owner-fields` keeps `required` fields
 * (including a React-rendered date input whose `required` nothing clears)
 * behind a `.hide` toggle, and a submit blocked on an invisible field would
 * refuse to send for no reason the user can see.
 *
 * Walks `display` up the ancestor chain rather than reading `offsetWidth` /
 * `getClientRects`, which are always zero-sized in jsdom and would make
 * every field count as hidden under Jest.
 */
function isVisible(field: ValidatableField): boolean {
  for (
    let node: HTMLElement | null = field;
    node !== null;
    node = node.parentElement
  ) {
    if (getComputedStyle(node).display === 'none') return false;
  }
  return true;
}

function paintValidity(field: ValidatableField): void {
  const valid = field.validity.valid;

  field.toggleAttribute('data-invalid', !valid);
  field.setAttribute('aria-invalid', String(!valid));

  const wrapper = errorWrapper(field);
  if (wrapper) wrapper.classList.toggle('error', !valid);

  const label = labelFor(field);
  if (label) {
    label.classList.toggle('error', !valid);
    if (valid) {
      label.removeAttribute('role');
    } else {
      label.setAttribute('role', 'alert');
    }
  }
}

/**
 * The element whose `.error` class reveals the `small.error` message: the
 * field's wrapper, or the wrapper around its `<label>` when the field sits
 * inside one. Never the form itself — the reveal selector
 * (`[data-validate] .error small.error`) matches any descendant, so `.error`
 * on the form would reveal every message in it at once.
 */
function errorWrapper(field: ValidatableField): HTMLElement | null {
  const parent = field.parentElement;
  if (!parent || parent instanceof HTMLFormElement) return null;
  if (parent instanceof HTMLLabelElement) {
    const grandparent = parent.parentElement;
    return grandparent instanceof HTMLFormElement ? null : grandparent;
  }
  return parent;
}

/**
 * The field's label, looked up inside its own form: `staff/csv_exports`
 * renders over a dozen forms on one page with `start_date` / `end_date` ids
 * repeated across them, so a document-wide query could colour another
 * form's label.
 */
function labelFor(field: ValidatableField): HTMLLabelElement | null {
  if (!field.id || !field.form) return null;
  return field.form.querySelector<HTMLLabelElement>(
    `label[for="${CSS.escape(field.id)}"]`
  );
}
